import { NavigateFunction, NavigateOptions } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import { ROUTES, USER_ID, CLIENT_ID, SITE_ID, SECTOR_ID, SKILL_ID, ABILITY_ID, EQUIPMENT_ID } from '../constants/navigation.constants';
import { CommonAuthService, i18n, EquipmentIcon } from '@umanforge/common';
import { AbilitiesIcon } from 'components/icons/abilities_icon.component';
import { ClientsIcon } from 'components/icons/clients_icon.component';
import { DisconnectIcon } from 'components/icons/disconnect_icon.component';
import { UmanforgeLogo } from 'components/icons/logos/umanforge_logo.component';
import { SectorsIcon } from 'components/icons/sectors_icon.component';
import { SitesIcon } from 'components/icons/sites_icon.component';
import { SkillsIcon } from 'components/icons/skills_icon.component';
import { UsersIcon } from 'components/icons/users_icon.component';
import { DriveIcon } from 'components/icons/drive.icon';
import { GlobalService } from './global.service';
import { Dispatch } from '@reduxjs/toolkit';
import { ConfirmNavModal } from 'components/modals/confirm_nav_modal.component';

export class NavigationService {

  private static modalRoot: ReactDOM.Root | null = null;

  static getNavBarItems( token: string | null, dispatch: Dispatch ) {
    return [
      {
        text: 'Home',
        logo: <UmanforgeLogo size={ 40 } className='fill-orange-base' />,
        link: ROUTES.HOME,
      },
      {
        text: 'User',
        logo: <div className="h-7 w-7 mt-2 fill-orange-base"><UsersIcon/></div>,
        link: ROUTES.USERS,
      },
      {
        text: 'Client',
        logo: <div className="h-7 w-7 -mt-1 fill-orange-base"><ClientsIcon/></div>,
        link: ROUTES.CLIENTS,
      },
      {
        text: 'Site',
        logo: <div className="h-7 w-7 -mt-1 fill-orange-base"><SitesIcon/></div>,
        link: ROUTES.SITES,
      },
      {
        text: 'Sector',
        logo: <div className="h-7 w-7 mt-2 fill-orange-base"><SectorsIcon/></div>,
        link: ROUTES.SECTORS,
      },
      {
        text: 'Authorization',
        logo: <div className="h-7 w-7 mt-2 fill-orange-base"><AbilitiesIcon/></div>,
        link: ROUTES.ABILITIES,
      },
      {
        text: 'Skill',
        logo: <div className="h-7 w-7 fill-orange-base"><SkillsIcon/></div>,
        link: ROUTES.SKILLS,
      },
      {
        text: 'common:equipment',
        logo: <div className="h-7 w-7 fill-orange-base"><EquipmentIcon/></div>,
        link: ROUTES.EQUIPMENTS,
      },
      {
        text: 'umandrive',
        logo: (
          <div className="h-7 w-7 fill-orange-base">
            <DriveIcon className="w-full h-full" />
          </div>
        ),
        link: ROUTES.DRIVE,
      },
      {
        text: 'Disconnection',
        logo: <div className="h-7 w-7 fill-orange-base"><DisconnectIcon/></div>,
        onClick: async () => {
          await CommonAuthService.signOutUser( token, dispatch, GlobalService.onAxiosWarning, err => { GlobalService.onAxiosError( err, dispatch ); } );
        },
      },
    ];
  }

  static toHome( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.HOME, needConfirmation, navigate );
  }

  static toFirstUserClient( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.FIRST_USER_CLIENT, needConfirmation, navigate );
  }

  static toUsers( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.USERS, needConfirmation, navigate );
  }

  static toUser( navigate: NavigateFunction, userId: string, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.USER.replace( USER_ID, userId ), needConfirmation, navigate );
  }

  static toNewUser( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.NEW_USER, needConfirmation, navigate );
  }

  static toClients( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.CLIENTS, needConfirmation, navigate );
  }

  static toClient( navigate: NavigateFunction, clientId: string, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.CLIENT.replace( CLIENT_ID, clientId ), needConfirmation, navigate );
  }

  static toNewClient( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.NEW_CLIENT, needConfirmation, navigate );
  }

  static toSites( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.SITES, needConfirmation, navigate );
  }

  static toSite( navigate: NavigateFunction, siteId: string, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.SITE.replace( SITE_ID, siteId ), needConfirmation, navigate );
  }

  static toNewSite( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.NEW_SITE, needConfirmation, navigate );
  }

  static toSectors( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.SECTORS, needConfirmation, navigate );
  }

  static toSector( navigate: NavigateFunction, sectorId: string, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.SECTOR.replace( SECTOR_ID, sectorId ), needConfirmation, navigate );
  }

  static toNewSector( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.NEW_SECTOR, needConfirmation, navigate );
  }

  static toSkills( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.SKILLS, needConfirmation, navigate );
  }

  static toSkill( navigate: NavigateFunction, skillId: string, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.SKILL.replace( SKILL_ID, skillId ), needConfirmation, navigate );
  }

  static toNewSkill( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.NEW_SKILL, needConfirmation, navigate );
  }

  static toAbilities( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.ABILITIES, needConfirmation, navigate );
  }

  static toAbility( navigate: NavigateFunction, abilityId: string, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.ABILITY.replace( ABILITY_ID, abilityId ), needConfirmation, navigate );
  }

  static toNewAbility( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.NEW_ABILITY, needConfirmation, navigate );
  }

  static toNewEquipment( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.NEW_EQUIPMENT, needConfirmation, navigate );
  }

  static toEquipment( navigate: NavigateFunction, equipmentId: string, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.EQUIPMENT.replace( EQUIPMENT_ID, equipmentId ), needConfirmation, navigate );
  }

  static toEquipments( navigate: NavigateFunction, needConfirmation: boolean ) {
    NavigationService.navigateTo( ROUTES.EQUIPMENTS, needConfirmation, navigate );
  }

  static toCloud( navigate: NavigateFunction, needConfirmation: boolean, option?: { clientId: string, siteId: string, cloudDirFileId: string } ) {
    let params = '';
    if ( option ) {
      params += `?clientId=${ option.clientId }&siteId=${ option.siteId }&cloudDirFileId=${ option.cloudDirFileId }`;
    }
    NavigationService.navigateTo( `${ ROUTES.DRIVE }${ params }`, needConfirmation, navigate );
  }

  static navigateTo( path: string, needConfirmation: boolean, navigate: NavigateFunction, option?: NavigateOptions ) {
    if ( NavigationService.isValidNavigation( path ) ) {
      if ( needConfirmation ) {
        NavigationService.openConfirmModal( path, navigate );
      } else {
        navigate( path, option );
      }
    }
  }

  private static isValidNavigation( path: string ) {
    return path != window.location.pathname;
  }

  private static openConfirmModal( path: string, navigate: NavigateFunction ) {
    const root = ( document.getElementById( 'root' ) as HTMLElement );

    let modalContainer = document.getElementById( 'confirm-nav-modal-container' );
    if ( !modalContainer ) {
      modalContainer = document.createElement( 'div' );
      modalContainer.id = 'confirm-nav-modal-container';
      root.appendChild( modalContainer );
    }

    const modal = (
      <ConfirmNavModal
        onConfirm={ () => { NavigationService.navigateTo( path, false, navigate ); } }
        onClose={ () => {
          if ( this.modalRoot ) {
            this.modalRoot.unmount();
            this.modalRoot = null;
          }
        } }
        t={ i18n.t }
      />
    );

    if ( !this.modalRoot ) {
      this.modalRoot = ReactDOM.createRoot( modalContainer );
    }

    this.modalRoot.render( modal );
  }

};